import { useContext, useEffect, useState } from "react";
import {
  getAgents,
  createAgent,
  updateAgent,
  deleteAgent,
  getAppsInfo,
} from "../services/console/agent";
import { AppContext } from "../context";

type UseType = "DATA" | "ACTIONS" | "INFO";
type ActionType = "CREATE" | "UPDATE" | "DELETE" | "REACTIVE";

const handleAgentsFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createAgent,
        fnMsg: t("Agents.agentsApiCreateLabel"),
      };
    case "UPDATE":
      return {
        actionFn: updateAgent,
        fnMsg: t("Agents.agentsApiUpdateLabel"),
      };
    case "DELETE":
      return {
        actionFn: deleteAgent,
        fnMsg: t("Agents.agentsApiDeleteLabel"),
      };
    case "REACTIVE":
        return {
          actionFn: updateAgent,
          fnMsg: t("Agents.agentsApiReactiveLabel"),
      };
    default:
      return {
        actionFn: () => {},
        fnMsg: "",
      };
  }
};

export const useAgents = (type: UseType, agentId?: number) => {
  const { t, toast, setAppState } = useContext(AppContext);
  const [data, setData] = useState<any | null>(null);
  const [reload, setReload] = useState<number>(0);

  const reloadFn = () => {
    setData(null);
    setReload((prev) => prev + 1);
  };

  const agentsAction = async (action: ActionType, data: any) => {
    const { actionFn, fnMsg } = handleAgentsFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(actionFn(data))
      .then((response) => {
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: `${fnMsg}`,
          life: 3000,
        });
        return "SUCCESS";
      })
      .catch((error) => {
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if (type === "DATA") {
      (async function () {
        await Promise.resolve(getAgents())
          .then((response) => {
            setData(response?.data);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [toast, reload, type, agentId]);

  useEffect(() => {
    if (type === "INFO") {
      (async function () {
        setAppState((prev) => ({
          ...prev,
          isLoading: true,
        }));
        await Promise.resolve(getAppsInfo())
          .then((response) => {
            setData(response?.data);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
        setAppState((prev) => ({
          ...prev,
          isLoading: false,
        }));
      })();
    }
  }, [toast, reload, type, agentId, setAppState]);

  return {
    data,
    agentsAction,
    reloadFn,
  };
};
